import React, { useState, useRef } from 'react';
import { 
  Paper, 
  Text, 
  Button, 
  Group, 
  Stack, 
  Avatar, 
  FileInput, 
  Progress,
  Alert,
  Textarea,
  Divider,
  Badge
} from '@mantine/core';
import { 
  IconUpload, 
  IconPhoto, 
  IconX, 
  IconCheck,
  IconAlertCircle,
  IconUser
} from '@tabler/icons-react';
import { uploadAvatarForUser } from '../../services/adminAvatarManager';
import { useUser } from '../../contexts/UserContext';

export const AvatarUploadForm = ({ selectedUser, onUploadSuccess }) => {
  const { profile } = useUser();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    // 파일 검증
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    
    if (!allowedTypes.includes(file.type)) {
      setUploadResult({
        success: false,
        error: '지원하지 않는 파일 형식입니다. (JPEG, PNG, WebP만 가능)'
      });
      return;
    }
    
    if (file.size > maxSize) {
      setUploadResult({
        success: false,
        error: '파일 크기가 너무 큽니다. (최대 5MB)'
      });
      return;
    }

    setSelectedFile(file);
    setUploadResult(null);
    
    // 미리보기 생성
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedUser || !profile) {
      setUploadResult({
        success: false,
        error: '파일과 사용자를 선택해주세요.'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      // 진행률 시뮬레이션
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const result = await uploadAvatarForUser(
        selectedUser.id, 
        selectedFile, 
        profile.id
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        setUploadResult({
          success: true,
          message: result.message,
          downloadURL: result.downloadURL
        });
        
        // 성공 시 폼 초기화
        setTimeout(() => {
          setSelectedFile(null);
          setPreviewUrl(null);
          setUploadProgress(0);
          setNotes('');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          onUploadSuccess && onUploadSuccess(selectedUser);
        }, 2000);
      } else {
        setUploadResult({
          success: false,
          error: result.error
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      // 구체적인 에러 메시지
      let errorMessage = '업로드 중 오류가 발생했습니다.';
      
      if (error.code === 'storage/unauthorized') {
        errorMessage = 'Storage 접근 권한이 없습니다. 관리자 권한을 확인해주세요.';
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage = 'Storage 용량이 초과되었습니다.';
      } else if (error.code === 'storage/retry-limit-exceeded') {
        errorMessage = '네트워크 오류로 업로드에 실패했습니다. 다시 시도해주세요.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setUploadResult({
        success: false,
        error: errorMessage
      });
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadProgress(0);
    setUploadResult(null);
    setNotes('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!selectedUser) {
    return (
      <Paper p="xl" withBorder>
        <Stack align="center" spacing="md">
          <IconUser size={48} color="gray" />
          <Text size="lg" color="dimmed" align="center">
            아바타를 업로드할 사용자를 선택해주세요
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack spacing="md">
        <Group position="apart" align="center">
          <Text size="lg" weight={600}>아바타 업로드</Text>
          {selectedUser.hasAvatar && (
            <Badge color="green" variant="light">
              이미 업로드됨
            </Badge>
          )}
        </Group>

        <Divider />

        {/* 선택된 사용자 정보 */}
        <Group>
          <Avatar 
            src={selectedUser.profile?.threads_profile_picture_url} 
            size="lg" 
            radius="xl"
            fallback={<IconUser size={24} />}
          />
          <Stack spacing="xs">
            <Text weight={500}>{selectedUser.profile?.name}</Text>
            <Text size="sm" color="dimmed">
              @{selectedUser.profile?.username}
            </Text>
            <Text size="xs" color="dimmed">
              ID: {selectedUser.profile?.id}
            </Text>
          </Stack>
        </Group>

        {/* 파일 선택 */}
        <FileInput
          ref={fileInputRef}
          label="아바타 이미지 선택"
          placeholder="이미지 파일을 선택하세요"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          value={selectedFile}
          onChange={handleFileSelect}
          icon={<IconPhoto size={16} />}
          disabled={uploading || selectedUser.hasAvatar}
          error={uploadResult?.success === false ? uploadResult.error : null}
        />

        {/* 미리보기 */}
        {previewUrl && (
          <Stack spacing="xs">
            <Text size="sm" weight={500}>미리보기:</Text>
            <Avatar 
              src={previewUrl} 
              size={120} 
              radius="xl"
              mx="auto"
            />
            <Text size="xs" color="dimmed" align="center">
              {selectedFile?.name} ({(selectedFile?.size / 1024 / 1024).toFixed(2)}MB)
            </Text>
          </Stack>
        )}

        {/* 업로드 진행률 */}
        {uploading && (
          <Stack spacing="xs">
            <Text size="sm" weight={500}>업로드 중...</Text>
            <Progress 
              value={uploadProgress} 
              label={`${uploadProgress}%`}
              size="lg"
              radius="xl"
            />
          </Stack>
        )}

        {/* 업로드 결과 */}
        {uploadResult && (
          <Alert
            icon={uploadResult.success ? <IconCheck size={16} /> : <IconAlertCircle size={16} />}
            title={uploadResult.success ? "업로드 성공" : "업로드 실패"}
            color={uploadResult.success ? "green" : "red"}
            variant="light"
          >
            {uploadResult.success ? uploadResult.message : uploadResult.error}
          </Alert>
        )}

        {/* 메모 입력 */}
        <Textarea
          label="업로드 메모 (선택사항)"
          placeholder="업로드 관련 메모를 입력하세요..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={uploading}
          minRows={2}
          maxRows={4}
        />

        {/* 액션 버튼 */}
        <Group position="apart">
          <Button
            variant="light"
            color="gray"
            leftIcon={<IconX size={16} />}
            onClick={handleReset}
            disabled={uploading}
          >
            초기화
          </Button>
          
          <Button
            variant="filled"
            leftIcon={<IconUpload size={16} />}
            onClick={handleUpload}
            loading={uploading}
            disabled={!selectedFile || selectedUser.hasAvatar}
          >
            {uploading ? '업로드 중...' : '업로드'}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}; 